import { useAuth } from "@/src/contexts/auth.context";
import { usePostCreationModal, VisibilityType } from "@/src/hooks/features/posts/use-post-creation-modal";
import { useLinkPreview } from "@/src/hooks/features/posts/use-link-preview";
import { LifeEventType, Post } from "@/src/models/post.model";
import {
  extractFirstUrl,
  detectMediaKind,
  normalizeUrl,
} from "@/src/utils/link-preview.utils";
import {
  ArrowDown3,
  AttachSquare,
  Camera,
  CloseCircle,
  Gallery,
  Global,
  Send,
  Heart,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  Platform
} from "react-native";
import LinkPreviewCard from "./post/link-preview/link-preview-card";
import { LinkPreviewSkeleton } from "./post/link-preview/link-preview-skeleton";
import { useTheme } from "@/src/styles/theme-provider";
import { useTheme as customTheme } from "@/src/styles/theme-provider";
import ScButton from "../ui-kit/button";
import { FileAttachment } from "./post/file-attachment";
import VideoPreview from "./videos/video-preview";
import VisibilityModal from "./visibility-modal/post-visibility-modal";
import PostBodyInput from "./tag-input";
import ItemSelectorModal from "../action-modals/item-selection/item-selection-modal";
import { useGetUsers, useGroupNamesByIds } from "@/src/services/api/groups/groups.hooks";
import {
  formatGroupAudience,
  VISIBILITY_LABEL_KEYS,
} from "@/src/utils/post-visibility.utils";
import { SelectableItem } from "../action-modals/item-selection/item-row";
import BlocksAvatar from "../ui-kit/blocks-avatar";
import MentionedUserPill from "./mentioned-user-pill";
import LifeUpdatePicker from "./life-update-picker";
import { HoverTooltip } from "../ui-kit/hover-tooltip";
import { formatUserName } from "@/src/utils/string.utils";

/**
 * Label-only tooltip for the composer's icon actions, which carry no visible
 * text of their own. Web-only by nature — HoverTooltip renders nothing without a
 * mouse, so native is unaffected.
 */
const ActionTooltip = ({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>) => (
  <HoverTooltip
    delay={200}
    content={<Text className="text-chalk text-xs">{label}</Text>}
  >
    {children}
  </HoverTooltip>
);

type CreatePostsProps = Readonly<{
  visible: boolean;
  insideGroup?: boolean;
  groupId?: string;
  onClose: () => void;
  onSubmit: (
    content: string,
    hashtags: string[],
    mentionedUsers: SelectableItem[],
    visibility: VisibilityType,
    attachmentUrls: string[],
    visibleToGroupIds?: string[],
    visibleToUserIds?: string[],
    groupId?: string,
    lifeEventType?: LifeEventType,
    lifeEventDate?: string,
  ) => void;
  post?: Post;
  isPosting?: boolean;
}>;

const CreatePostModal = ({
  visible,
  insideGroup,
  groupId,
  onClose,
  onSubmit,
  post,
  isPosting,
}: CreatePostsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { themeVars, varStyle } = useTheme();
  const postCreationModal = usePostCreationModal(
    post,
    visible,
    onSubmit,
    onClose,
    isPosting,
    insideGroup,
    groupId,
  );

  const [userSearch, setUserSearch] = useState("");
  const {
    data: usersData,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: usersHasNextPage,
    isFetchingNextPage: usersIsFetchingNextPage,
  } = useGetUsers(userSearch);

  // Derive a link preview from the composer content, so the author sees the
  // card before posting. Direct image/video URLs render inline (no metadata
  // fetch); page URLs go through the extraction worker via useLinkPreview.
  const { data: linkPreview, isFetching: isLinkPreviewLoading } =
    useLinkPreview(postCreationModal.content ?? "");
  const composerPreviewUrl = extractFirstUrl(postCreationModal.content ?? "");
  const composerMediaKind = composerPreviewUrl ? detectMediaKind(composerPreviewUrl) : null;
  // Like the Selisian composer: the author can dismiss a page card (×), and the
  // card stays out of the way once attachments are added.
  const [dismissedPreviewUrls, setDismissedPreviewUrls] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const composerPreviewKey = composerPreviewUrl ? normalizeUrl(composerPreviewUrl) : null;
  const showComposerPagePreview =
    composerMediaKind === "page" &&
    !!composerPreviewKey &&
    !dismissedPreviewUrls.has(composerPreviewKey) &&
    postCreationModal.selectedMedia.length === 0 &&
    postCreationModal.selectedFiles.length === 0;
  const dismissComposerPreview = () => {
    if (!composerPreviewKey) return;
    setDismissedPreviewUrls((prev) => new Set(prev).add(composerPreviewKey));
  };

  /**
   * The visibility chip names the groups actually chosen instead of the generic
   * "Group", so the author can see where the post is going — during creation and
   * when editing an existing post, which arrives with its groups preselected.
   *
   * Only the selected ids are looked up, so the request is scoped to what is on
   * screen; the chip falls back to the generic label until it resolves.
   */
  const selectedGroupIds = postCreationModal.selectedGroupIds ?? [];
  const { data: selectedGroupNames } = useGroupNamesByIds(
    postCreationModal.visibility === "group" ? selectedGroupIds : [],
  );
  const genericVisibilityKey =
    VISIBILITY_LABEL_KEYS[postCreationModal.visibility] ?? "LABEL_PUBLIC";
  const visibilityLabel =
    postCreationModal.visibility === "group"
      ? (formatGroupAudience(
          selectedGroupIds.map((id) => selectedGroupNames?.[id] ?? ""),
        ) ?? t(genericVisibilityKey))
      : t(genericVisibilityKey);

  const users: SelectableItem[] = (
    usersData?.pages.flatMap((page) => page?.users ?? []) ?? []
  ).map((user) => ({
    itemId: user.itemId,
    displayName: user.displayName,
    userName: user.userName,
    profileImageUrl: user.profileImageUrl,
  }));

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View className="flex-1 justify-center items-center bg-black/40" style={themeVars as any}>
        <View className="bg-chalk rounded-2xl gap-4 w-[80%] md:w-[50%]">
          {/* Header */}
          <View className="flex-row justify-between items-start p-3 border-b border-ash-3">
            <View className="flex-row items-center gap-3">
              {/* Interpolating into a template literal turned a missing picture
                  into the literal string "undefined", which is a guaranteed
                  broken image. BlocksAvatar resolves the URL and falls back to
                  initials. */}
              <BlocksAvatar
                userId={user?.itemId}
                fallbackImageUrl={user?.profileImageUrl}
                firstName={user?.firstName}
                lastName={user?.lastName}
                size={40}
                className="rounded-full"
              />
              <View className="gap-1">
                <Text className="font-medium text-sm text-onyx">
                  {formatUserName(user?.firstName, user?.lastName)}
                </Text>
                <Pressable
                  className="flex-row items-center gap-1 border border-ash-3 px-2 py-1 rounded-[8px] hover:bg-ash-3"
                  onPress={() => {
                    postCreationModal.setShowVisibilityModal(true);
                  }}
                >
                  <Global size={16} color={themeVars["--color-slate-default"] as string} />
                  <Text
                    className="text-xs text-gray-dark flex-shrink min-w-0"
                    numberOfLines={1}
                  >
                    {visibilityLabel}
                  </Text>
                  <ArrowDown3 size={16} color={themeVars["--color-slate-default"] as string} />
                </Pressable>
              </View>
            </View>
            <Pressable
              onPress={postCreationModal.handleClose}
              className="p-1.5 rounded-full hover:bg-ash-3"
            >
              <CloseCircle size={30} color={themeVars["--color-slate-default"] as string} />
            </Pressable>
          </View>
          <PostBodyInput
            value={postCreationModal.content}
            hashtags={postCreationModal.hashtags}
            onChange={postCreationModal.handleContentChange}
            onTagsChange={postCreationModal.setHashtags}
          />

          {/* Composer link preview.
              Direct image/video: rendered straight from the URL (no metadata fetch).
              HTML-page: a skeleton while the extractor works, then LinkPreviewCard;
              nothing at all on failure (the plain link text remains in the input —
              no broken card, no layout shift).
              Preview resolution is async and never blocks the Post action. */}
          {composerPreviewUrl && composerMediaKind === 'image' && (
            <View
              className="mx-4 mb-2 rounded-xl overflow-hidden"
              testID="composer-image-preview"
            >
              <Image
                source={{ uri: composerPreviewUrl }}
                style={{ width: '100%', height: 200 }}
                resizeMode="cover"
                accessibilityIgnoresInvertColors
              />
            </View>
          )}
          {composerPreviewUrl && composerMediaKind === 'video' && (
            <View className="mx-4 mb-2" testID="composer-video-preview">
              <VideoPreview uri={composerPreviewUrl} />
            </View>
          )}
          {showComposerPagePreview && linkPreview && (
            <View testID="composer-page-preview">
              <LinkPreviewCard preview={linkPreview} compact onDismiss={dismissComposerPreview} />
            </View>
          )}
          {showComposerPagePreview &&
            !linkPreview &&
            isLinkPreviewLoading && (
              <View testID="composer-page-preview-loading">
                <LinkPreviewSkeleton />
              </View>
            )}

          {postCreationModal.mentionedUsers?.map((user) => (
            <View key={user.itemId} className="flex-row flex-wrap gap-2 px-3">
              <MentionedUserPill
                displayName={user.displayName ?? "Unknown"}
                onRemove={() => postCreationModal.setMentionedUsers((prev) => prev.filter((u) => u.itemId !== user.itemId))}
                userId={user.userId}
                isEditing={true}
              />
            </View>
          ))}

          {/* Media previews */}
          {postCreationModal.selectedMedia.length > 0 && (
            <View className="flex-row mt-2 px-3" style={themeVars as any}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {postCreationModal.selectedMedia.map((media, idx) =>
                  media.type?.startsWith("video/") ? (
                    <View key={idx} className="w-[100px] mr-2">
                      <VideoPreview
                        uri={media.uri}
                        onRemove={() =>
                          postCreationModal.handleRemoveMedia(idx)
                        }
                        closeButton={true}
                      />
                    </View>
                  ) : (
                    <View key={idx} className="mr-2">
                      <Image
                        source={{ uri: media.uri }}
                        style={{ width: 100, height: 100, borderRadius: 8 }}
                      />
                      <Pressable
                        onPress={() => postCreationModal.handleRemoveMedia(idx)}
                        style={{ backgroundColor: themeVars["--color-slate-default"] as string }}
                        className="absolute top-1 right-1 rounded-full hover:bg-ash-3"
                      >
                        <CloseCircle size={16} color="white" />
                      </Pressable>
                    </View>
                  ),
                )}
              </ScrollView>
            </View>
          )}

          {/* Attached files */}
          {postCreationModal.selectedFiles.length > 0 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              className="px-4"
            >
              <View className="gap-2 flex-row">
                {postCreationModal.selectedFiles.map((file) => (
                  <FileAttachment
                    key={file.uri}
                    file={file}
                    onRemove={() =>
                      postCreationModal.handleRemoveFile(file.uri)
                    }
                  />
                ))}
              </View>
            </ScrollView>
          )}

          {/* Actions */}
          <View className="flex-row justify-between items-center py-2 px-3 border-t border-ash-3 gap-4 flex-wrap">
            <View className="flex-row gap-2">
              {Platform.OS !== "web" && (
                <Pressable
                  onPress={postCreationModal.handleCameraPress}
                  className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
                >
                  <Camera size={26} color={themeVars["--color-primary-default"] as string} />
                </Pressable>
              )}
              <ActionTooltip label={t("LABEL_PHOTOS")}>
                <Pressable
                  onPress={postCreationModal.handlePickImage}
                  className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
                >
                  <Gallery size={26} color={themeVars["--color-primary-default"] as string} />
                </Pressable>
              </ActionTooltip>
              <ActionTooltip label={t("LABEL_ATTACHMENT")}>
                <Pressable
                  onPress={postCreationModal.handlePickFile}
                  className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
                >
                  <AttachSquare size={26} color={themeVars["--color-primary-default"] as string} />
                </Pressable>
              </ActionTooltip>
              <ActionTooltip label={t("LABEL_LIFE_UPDATE")}>
                <Pressable
                  onPress={() => postCreationModal.setShowLifeUpdatePicker(true)}
                  className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
                >
                  {/* Always primary — the Bold/Outline variant carries the
                      selected state, so colour no longer has to. */}
                  <Heart
                    size={26}
                    color={themeVars["--color-primary-default"] as string}
                    variant={postCreationModal.lifeEventType ? "Bold" : "Outline"}
                  />
                </Pressable>
              </ActionTooltip>
            </View>

            <ScButton
              onPress={postCreationModal.handleSubmit}
              loading={postCreationModal.isLoading}
              disabled={isPosting || !postCreationModal.hasContent}
              style={{
                backgroundColor: postCreationModal.hasContent
                  ? themeVars["--color-primary-default"] as string
                  : themeVars["--color-ash-2"] as string,
              }}
              icon={
                isPosting ? (
                  <ActivityIndicator size={16} color="white" />
                ) : (
                  <Send color="white" />
                )
              }
              iconPosition="right"
              text={
                isPosting ? "BTN_POSTING" : post ? "BTN_UPDATE" : "BTN_POST"
              }
            />
          </View>
        </View>
      </View>

      <VisibilityModal
        visible={postCreationModal.showVisibilityModal}
        onClose={() => postCreationModal.setShowVisibilityModal(false)}
        selectedGroupIds={postCreationModal.selectedGroupIds}
        selectedUserIds={postCreationModal.selectedUserIds}
        setSelectedGroupIds={postCreationModal.setSelectedGroupIds}
        setSelectedUserIds={postCreationModal.setSelectedUserIds}
        toggleGroup={postCreationModal.toggleGroup}
        toggleUser={postCreationModal.toggleUser}
        visibility={postCreationModal.visibility}
        onVisibilityChange={postCreationModal.setVisibility}
      />

      {/* Paged 10 at a time — see the note on the comment mention picker. */}
      <ItemSelectorModal
        items={users}
        visible={postCreationModal.showMentionModal}
        titleKey="LABEL_MENTION_USERS"
        confirmButtonKey="LABEL_ADD"
        onClose={postCreationModal.handleMentionCancel}
        onConfirm={(_ids, items) => { postCreationModal.handleMentionConfirm(items); }}
        onEndReached={() => {
          if (usersHasNextPage && !usersIsFetchingNextPage) fetchNextUsersPage();
        }}
        isFetchingMore={usersIsFetchingNextPage}
        onSearchChange={setUserSearch}
        totalCount={usersData?.pages[0]?.count}
      />

      <LifeUpdatePicker
        visible={postCreationModal.showLifeUpdatePicker}
        selectedType={postCreationModal.lifeEventType}
        selectedDate={postCreationModal.lifeEventDate}
        onSelectType={postCreationModal.setLifeEventType}
        onSelectDate={postCreationModal.setLifeEventDate}
        onConfirm={() => postCreationModal.setShowLifeUpdatePicker(false)}
        onClose={() => {
          postCreationModal.setLifeEventType(undefined);
          postCreationModal.setLifeEventDate(undefined);
          postCreationModal.setShowLifeUpdatePicker(false);
        }}
      />
    </Modal>
  );
};

export default CreatePostModal;
